import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { WeezHeader, Icon, Numbers, DropdownItem, TooltipHelp, Status, ToastContext } from '@weezevent/nacre';
import RatesManager, { generateClassNameRateName } from '../../rates/components/rateManager';
import { InheritedProperty } from '../../rates/ratesList';
import { compute_sum_float } from '../../rates/utils';
import { useFetchStatistics } from '../../attendees_stats/components/statisticsFetcher';
import RateModel from '../../../models/rateModel';
import StyleRM from '../../rates/components/rateManager.module.css';

export default function ResaleActivation({ organization, event }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [stats, setStats] = React.useState(null);

    useFetchStatistics({
        route: `/ticket/organizations/${organization.id}/events/${event.id}/stats`,
        queryParams: {
            stats: 'sales_by_resale_stats'
        },
        onStats: stats => {
            setStats(stats);
        },
        onError: () => toast.error(t('ticket.eventDashboard.statistics.error_retrieving_stats'))
    });

    const getResaleStatsForRate = (rateId, stats) => {
        if (!stats) return null;

        const resaleStats = stats[0];
        if (!resaleStats) return null;

        const allGroups = resaleStats.value.root || [];
        const allRates = [...(resaleStats.value.rates_without_group || []), ...allGroups.flatMap(group => group.rates || [])];

        return allRates.find(r => r.id === rateId) || null;
    };

    function ResaleRateName({ rate, groups }) {
        const [targetRate, setTargetRate] = React.useState(null);

        RateModel.useApiModel(
            {
                organizationId: organization.id,
                eventId: event.id,
                id: rate?.resale_config?.resale_rate,
                launch: Boolean(rate?.resale_config?.resale_rate),
                onSuccess: r => setTargetRate(r)
            },
            [organization, event, rate]
        );

        return (
            <>
                <div className={StyleRM[generateClassNameRateName(rate, groups)]}>{rate.name}</div>
                <div className={StyleRM['rates-table__cell-target-rate']}>{targetRate?.name || '-'}</div>
            </>
        );
    }

    const tableCells = [
        {
            header: (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {t('ticket.resales.activation.headers.name')}
                    <TooltipHelp triggerOnHover title={t('ticket.resales.activation.headers.name-tooltip')} />
                </div>
            ),
            renderRate: (rate, { groups }) => {
                return <InheritedProperty property="name" object={rate} handleProperty={() => <ResaleRateName rate={rate} groups={groups} />} template="rate_template" />;
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                    </div>
                );
            }
        },
        {
            header: t('ticket.resales.activation.headers.original-price'),
            renderRate: (rate, { event }) => {
                let _price = rate.rate_template?.price || rate.price;
                const computedPrice = rate.fees?.length && rate.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(_price || 0));
                if (!rate.resale_config) return '-';
                return (
                    <InheritedProperty
                        property="price"
                        object={rate}
                        handleProperty={price => (price !== '0.00' ? <Numbers value={computedPrice || price} currency={event.currency} showNullDecimal /> : t('ticket.rates.free'))}
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.resales.activation.headers.resale-price'),
            renderRate: (rate, { event }) => {
                let max_price = rate.resale_config?.max_price;
                let min_price = rate.resale_config?.min_price;
                if (!rate.resale_config) return '-';
                return (
                    <InheritedProperty
                        property="price"
                        object={rate}
                        handleProperty={() => (
                            <>
                                <Numbers value={min_price} currency={event.currency} showNullDecimal />
                                {' / '}
                                <Numbers value={max_price} currency={event.currency} showNullDecimal />
                            </>
                        )}
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.resales.activation.headers.fees'),
            renderRate: (rate, { event }) => {
                const fees = rate.resale_config?.resaler_fee || 0;
                if (!rate.resale_config) return '-';
                return (
                    <InheritedProperty
                        property="fees"
                        object={rate}
                        handleProperty={() => <Numbers value={fees} currency={event.currency} showNullDecimal />}
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.resales.activation.headers.sold'),
            renderRate: rate => {
                return (
                    <InheritedProperty
                        property="id"
                        object={rate}
                        handleProperty={id => {
                            const statRate = getResaleStatsForRate(id, stats);
                            if (!statRate) return '-';

                            const sold = statRate.sold || 0;
                            const total = (statRate.on_sale || 0) + (statRate.sale_in_progress || 0) + sold;

                            return `${sold} / ${total}`;
                        }}
                        template="rate_template"
                    />
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.resales.activation.headers.resale'),
            renderRate: rate => {
                const isActive = rate.resale_config && rate.resale_config.activated;
                const status = isActive
                    ? { color: 'green', text: t('ticket.resales.activation.status.active') }
                    : { color: 'orange', text: t('ticket.resales.activation.status.inactive') };

                return <Status color={status.color} text={status.text} />;
            },
            renderGroup: () => null
        }
    ];

    function onRowClick(rate) {
        history.push(`/ticket/O${organization.id}/E${event.id}/resale/${rate.id}/edit`);
    }

    const customActions = rate => <DropdownItem item={t('ticket.resales.activation.configure')} onClick={() => onRowClick(rate)} />;

    return (
        <div>
            <WeezHeader title={t('ticket.resales.activation.title')}>{t('ticket.resales.activation.subtitle')}</WeezHeader>
            <RatesManager
                event={event}
                organization={organization}
                initialQuery={{
                    organizationId: organization.id,
                    eventId: event.id
                }}
                selectable={false}
                tableCells={tableCells}
                customActions={customActions}
                mode="resale"
                onRowClick={onRowClick}
                hideEmptyGroup
            />
        </div>
    );
}
